import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import type { Experiments } from '@wix/yoshi-flow-editor';
import type { ISettingsGetter } from '@wix/tpa-settings';
import { ClickTarget, CourseClickTarget } from '../../types/types';
import type { EnrichedService } from '../../types/types';
import settingsParams from '../../components/BookOnline/settingsParams';
import {
  BOOK_BUTTON_DESTINATION_EXPERIMENT,
  ENABLE_COURSE_DIRECT_NAVIGATION_TO_FORM,
} from '../../consts';
import { isTooLateToBook } from '../serviceDetails/displayOptions';

/**
 * The resolved destination of a service-list click, shared by the click handlers
 * (onServiceClick / onActionButtonClick) and the presentation hooks that derive the
 * anchor href / tab index. Keeping this in one place prevents the href from drifting
 * out of sync with where the click actually navigates.
 */
export enum ClickDestination {
  SERVICE_PAGE = 'SERVICE_PAGE',
  BOOKING_CALENDAR = 'BOOKING_CALENDAR',
  BOOKING_FORM = 'BOOKING_FORM',
}

// Both the controller (`flowAPI`) and the components (via `useExperiments()` /
// `useSettings()`) satisfy these, so a single resolver serves every caller.
export interface ClickDestinationContext {
  service: EnrichedService;
  experiments: Experiments;
  settings: { get: ISettingsGetter };
  // Course availability (schedule capacity − participants). Only meaningful for
  // courses; `undefined` while it is still loading, which is treated as "not
  // fully booked" so a click never blocks on availability that hasn't arrived.
  numberOfSpotsLeft?: number;
}

const resolveCourseDestination = (
  courseClickTarget: CourseClickTarget,
  service: EnrichedService,
  numberOfSpotsLeft?: number,
): ClickDestination => {
  // A fully booked course can't be booked — its Service Page shows the "fully
  // booked" message and disables Book Now — so route it there instead of a
  // dead-end Booking Form, the same as a course that is too late to join.
  const isFullyBooked =
    numberOfSpotsLeft !== undefined && numberOfSpotsLeft <= 0;

  return courseClickTarget === CourseClickTarget.BOOKING_FORM &&
    !isTooLateToBook(service) &&
    !isFullyBooked
    ? ClickDestination.BOOKING_FORM
    : ClickDestination.SERVICE_PAGE;
};

interface ClickDestinationTargets {
  // The `courseServiceClickTarget` / `courseBookButtonClickTarget` setting,
  // consulted for courses when the direct-navigation FT is on.
  courseClickTarget: typeof settingsParams.courseServiceClickTarget;
  // The `serviceClickTarget` / `bookButtonClickTarget` setting, consulted for
  // appointments & classes.
  clickTarget: typeof settingsParams.serviceClickTarget;
  // When set, the non-course `clickTarget` is only honored while this experiment
  // is on; otherwise those services default to the booking flow.
  clickTargetExperiment?: string;
}

const getClickDestination = (
  {
    service,
    experiments,
    settings,
    numberOfSpotsLeft,
  }: ClickDestinationContext,
  {
    courseClickTarget,
    clickTarget,
    clickTargetExperiment,
  }: ClickDestinationTargets,
): ClickDestination => {
  if (service.displayOnlyNoBookFlow) {
    return ClickDestination.SERVICE_PAGE;
  }

  if (service.type === ServiceType.COURSE) {
    return experiments.enabled(ENABLE_COURSE_DIRECT_NAVIGATION_TO_FORM)
      ? resolveCourseDestination(
          settings.get(courseClickTarget),
          service,
          numberOfSpotsLeft,
        )
      : ClickDestination.SERVICE_PAGE;
  }

  const honorsClickTarget =
    !clickTargetExperiment || experiments.enabled(clickTargetExperiment);

  return honorsClickTarget &&
    settings.get(clickTarget) === ClickTarget.SERVICE_PAGE
    ? ClickDestination.SERVICE_PAGE
    : ClickDestination.BOOKING_CALENDAR;
};

/**
 * Destination when clicking a service name or image.
 * - Appointments & classes follow `serviceClickTarget` (calendar vs service page).
 * - Courses follow `courseServiceClickTarget` (form vs service page) when the FT is on,
 *   and always go to the service page when it is off.
 */
export const getServiceNameClickDestination = (
  context: ClickDestinationContext,
): ClickDestination =>
  getClickDestination(context, {
    courseClickTarget: settingsParams.courseServiceClickTarget,
    clickTarget: settingsParams.serviceClickTarget,
  });

/**
 * Destination when clicking the Book button.
 * - Appointments & classes follow `bookButtonClickTarget`, but only when the
 *   bookButtonDestination experiment is on; otherwise they default to the booking flow.
 * - Courses follow `courseBookButtonClickTarget` (form vs service page) when the FT is on,
 *   and always go to the service page when it is off.
 */
export const getBookButtonClickDestination = (
  context: ClickDestinationContext,
): ClickDestination =>
  getClickDestination(context, {
    courseClickTarget: settingsParams.courseBookButtonClickTarget,
    clickTarget: settingsParams.bookButtonClickTarget,
    clickTargetExperiment: BOOK_BUTTON_DESTINATION_EXPERIMENT,
  });
